import { useState } from "react";
import { Link } from "react-router";
import { stripMarkdown } from "@astrum/shared";
import { Avatar, Icon, IconButton, Menu, Pill, useToast } from "@astrum/ui";
import type { MenuItem, PillTone } from "@astrum/ui";
import type { ForwardedFrom, LinkPreview, Message, MessageFlagKind } from "../../lib/api.js";
import { StatusMark } from "../../components/StatusMark.js";
import { EmojiPickerPopover } from "../../components/EmojiPickerPopover.js";
import { formatClock } from "../../lib/chat-format.js";
import { copyImageToClipboard } from "../../lib/copy-image.js";
import { REMINDER_PRESETS } from "../../lib/reminder-presets.js";
import { AttachmentViewer, MessageAttachments } from "./AttachmentViewer.js";
import type { ViewedAttachment } from "./AttachmentViewer.js";
import { MessageBody } from "./MessageBody.js";
import { useMessageActions } from "./message-actions.js";
import { INLINE_IMAGE_TYPES } from "./types.js";
import type { PendingMessage } from "./types.js";

const STATUS_TONE: Record<string, PillTone> = {
  todo: "neutral",
  in_progress: "accent",
  review: "info",
  done: "ok",
  cancelled: "neutral",
};

const PRIORITY_TONE: Record<string, PillTone> = {
  low: "neutral",
  normal: "neutral",
  medium: "warn",
  high: "danger",
  urgent: "danger",
};

/** Only http(s) ever becomes a link: the url came from a message body, so it is untrusted. */
function safeHref(url: string): string | null {
  try {
    const parsed = new URL(url);
    return parsed.protocol === "http:" || parsed.protocol === "https:" ? parsed.href : null;
  } catch {
    return null;
  }
}

/**
 * The quoted original of a forward. The source's name and a link back are shown
 * only when the server said the viewer can read it (`channelId` set); otherwise
 * it is just the author and the quoted text, so nothing leaks.
 */
export function ForwardedBlock({ from }: { from: ForwardedFrom }) {
  const where = from.channelName ? `#${from.channelName}` : from.channelId ? "a direct message" : null;
  const label = (
    <>
      <Icon name="forward" size={12} />
      Forwarded{where ? ` from ${where}` : ""} · {from.authorName}
    </>
  );
  return (
    <div className="chat-message__forward-block">
      {from.channelId ? (
        <Link
          className="chat-message__forward-source"
          to={from.messageId ? `/chat/${from.channelId}?message=${from.messageId}` : `/chat/${from.channelId}`}
        >
          {label}
        </Link>
      ) : (
        <span className="chat-message__forward-source">{label}</span>
      )}
      {from.quote && <div className="chat-message__forward-quote">{stripMarkdown(from.quote)}</div>}
    </div>
  );
}

/** A text-only card: title, description and site. No image is fetched or hotlinked. */
export function LinkPreviewCard({
  preview,
  onRemove,
}: {
  preview: LinkPreview;
  onRemove?: () => void;
}) {
  const href = safeHref(preview.url);
  if (!href || !(preview.title || preview.description)) return null;
  return (
    <div className="chat-link-preview">
      {preview.siteName && <span className="chat-link-preview__site">{preview.siteName}</span>}
      <a className="chat-link-preview__title" href={href} target="_blank" rel="noopener noreferrer nofollow">
        {preview.title ?? href}
      </a>
      {preview.description && <p className="chat-link-preview__desc">{preview.description}</p>}
      {onRemove && (
        <button type="button" className="chat-link-preview__remove" onClick={onRemove}>
          Remove preview
        </button>
      )}
    </div>
  );
}

function LinkedTaskCard({ task }: { task: NonNullable<Message["linkedTask"]> }) {
  const isDone = task.status === "done";
  return (
    <div className="chat-task-card">
      <span
        className={`chat-task-card__check ${isDone ? "is-done" : ""}`}
        role="img"
        aria-label={isDone ? "Task complete" : "Task incomplete"}
      >
        {isDone && <Icon name="check" size={11} strokeWidth={3} />}
      </span>
      <div className="chat-task-card__body">
        <strong className={isDone ? "is-done" : ""}>{task.title}</strong>
        <div className="chat-task-card__meta">
          <Pill tone={STATUS_TONE[task.status] ?? "neutral"}>{task.status.replace("_", " ")}</Pill>
          <Pill tone={PRIORITY_TONE[task.priority] ?? "neutral"}>{task.priority}</Pill>
          {task.assigneeName && <span className="chat-task-card__assignee">{task.assigneeName}</span>}
        </div>
      </div>
    </div>
  );
}

/* ──────────────────────────────────── message row ──────────────────────────────────── */

interface MessageRunProps {
  authorName: string;
  authorId: string;
  messages: PendingMessage[];
}

const FLAG_LABEL: Record<MessageFlagKind, string> = {
  flag: "Flagged",
  todo: "To-do",
  done: "Done",
};

/**
 * The flag control: an icon that opens a menu offering the lifecycle, plus
 * one item for "not flagged at all" once something is already set.
 *
 * A menu rather than a single toggle button because there are three states
 * to choose between, not one to switch on and off — a click needs to say
 * which of "come back to this", "this needs doing" and "done" is meant,
 * which a plain toggle cannot express.
 */
function FlagMenu({ myFlag, onSetFlag }: { myFlag: MessageFlagKind | null; onSetFlag: (kind: MessageFlagKind | null) => void }) {
  const [open, setOpen] = useState(false);

  const items: MenuItem[] = [
    { id: "flag", label: "Flag", icon: "flag", disabled: myFlag === "flag", onSelect: () => onSetFlag("flag") },
    { id: "todo", label: "Mark as to-do", icon: "check", disabled: myFlag === "todo", onSelect: () => onSetFlag("todo") },
    { id: "done", label: "Mark done", icon: "check", disabled: myFlag === "done", onSelect: () => onSetFlag("done") },
  ];
  if (myFlag) {
    items.push({ id: "clear", label: "Clear flag", destructive: true, onSelect: () => onSetFlag(null) });
  }

  return (
    <Menu open={open} onClose={() => setOpen(false)} label="Flag this message" items={items}>
      <IconButton
        icon={<Icon name="flag" size={14} />}
        label={myFlag ? `Change flag (currently ${FLAG_LABEL[myFlag]})` : "Flag this message"}
        size="sm"
        onClick={() => setOpen((v) => !v)}
      />
    </Menu>
  );
}

/**
 * "Remind me about this message." A menu of quick presets plus a custom
 * date/time; each resolves to an absolute instant (computed in the viewer's
 * local clock) that the caller sends to the API.
 */
function RemindMenu({ onRemind }: { onRemind: (at: Date) => void }) {
  const [open, setOpen] = useState(false);

  const items: MenuItem[] = REMINDER_PRESETS.map((preset) => ({
    id: preset.id,
    label: preset.label,
    icon: preset.id === "custom" ? "calendar" : "clock",
    onSelect: () => {
      const at = preset.at();
      if (at) {
        onRemind(at);
        return;
      }
      // Custom: prompt for a datetime-local value, interpreted in local time.
      const raw = window.prompt("Remind me at (YYYY-MM-DD HH:MM):");
      if (!raw) return;
      const parsed = new Date(raw.replace(" ", "T"));
      if (Number.isNaN(parsed.getTime()) || parsed.getTime() <= Date.now()) {
        window.alert("Please enter a valid future date and time.");
        return;
      }
      onRemind(parsed);
    },
  }));

  return (
    <Menu open={open} onClose={() => setOpen(false)} label="Remind me about this message" items={items}>
      <IconButton
        icon={<Icon name="clock" size={14} />}
        label="Remind me about this message"
        size="sm"
        onClick={() => setOpen((v) => !v)}
      />
    </Menu>
  );
}

export function MessageRun({ authorId, authorName, messages }: MessageRunProps) {
  const { toast } = useToast();
  const actions = useMessageActions();
  const { viewerId, canRaiseTask, canPin, readWatermark, jumpedId } = actions;
  /* Edit and delete are offered on the whole run, since a run is by definition one author. */
  const isOwn = authorId === viewerId;
  const [viewing, setViewing] = useState<ViewedAttachment | null>(null);
  /* The message whose reaction picker is open, if any. */
  const [reactingId, setReactingId] = useState<string | null>(null);

  return (
    <div className="chat-run">
      <Avatar name={authorName} size={32} className="chat-run__avatar" />
      <div className="chat-run__body">
        <div className="chat-run__header">
          <span className="chat-run__name">{authorName}</span>
          <StatusMark userId={authorId} fetched={messages[0]!.authorStatus} />
          <time dateTime={messages[0]!.createdAt}>{formatClock(messages[0]!.createdAt)}</time>
        </div>
        {messages.map((message) =>
          /*
           * A withdrawn message renders as a tombstone and nothing else — no
           * quote, no attachment, no hover actions. Returning early rather than
           * conditioning each part keeps that guarantee in one place: a new
           * feature added below cannot accidentally appear on a deleted row.
           */
          message.deletedAt ? (
            <div key={message.id} className="chat-message chat-message--deleted">
              <p className="chat-message__body">Message deleted</p>
            </div>
          ) : (
          <div
            key={message.id}
            // Addressable, so a search result has somewhere to scroll to.
            id={`message-${message.id}`}
            className={`chat-message ${message.pending ? "is-pending" : ""} ${message.failed ? "is-failed" : ""} ${
              jumpedId === message.id ? "is-jumped" : ""
            }`}
          >
            {/* No attribution to where from — the source may be a channel
                this message's readers cannot see, so only the fact of
                forwarding is shown, matching WhatsApp's own plain
                "Forwarded" tag. */}
            {message.isForwarded && !message.forwardedFrom && (
              <div className="chat-message__forwarded">
                <Icon name="forward" size={12} />
                Forwarded
              </div>
            )}

            {message.forwardedFrom && <ForwardedBlock from={message.forwardedFrom} />}

            {/* What this replies to, above the reply itself. Absent when the
                parent has been deleted, so a reply to a removed message reads
                as an ordinary one rather than quoting a blank. */}
            {message.parent && (
              <div className="chat-message__quote">
                <span className="chat-message__quote-author">{message.parent.authorName}</span>
                <span className="chat-message__quote-body">{stripMarkdown(message.parent.body)}</span>
              </div>
            )}

            {/*
              * A share whose text is just its filename says nothing the
              * attachment below does not already show, so the line is dropped
              * rather than printed twice. A share with a real comment keeps it.
              *
              * The body still exists on the message — it is what a notification
              * preview or a search result has to work with — it is only this
              * view that has something better to show.
              */}
            {!message.attachments?.some((a) => a.fileName === message.body) && (
              <div className="chat-message__body">
                <MessageBody body={message.body} viewerId={viewerId} />
              </div>
            )}

            <MessageAttachments attachments={message.attachments} onView={setViewing} />

            {message.linkPreview && (
              <LinkPreviewCard
                preview={message.linkPreview}
                onRemove={isOwn && !message.pending ? () => actions.removePreview(message.id) : undefined}
              />
            )}

            {message.linkedTask && <LinkedTaskCard task={message.linkedTask} />}

            {/* Like a flag, a pin is meant to be seen without hovering. */}
            {message.pinned && (
              <span className="chat-message__pin-marker">
                <Icon name="pin" size={12} />
                Pinned
              </span>
            )}

            {/*
             * Unlike the other actions below, a flag's badge stays visible
             * without hovering — that is the point of flagging something: to
             * find it again later, at a glance, in a channel scrolled past.
             */}
            {message.myFlag && (
              <button
                type="button"
                className={`chat-message__flag-badge is-${message.myFlag}`}
                onClick={() => actions.setFlag(message.id, message.myFlag === "done" ? null : "done")}
                title={
                  message.myFlag === "done"
                    ? "Marked done — click to clear"
                    : "Click to mark done"
                }
              >
                <Icon name="flag" size={12} />
                {FLAG_LABEL[message.myFlag]}
              </button>
            )}

            <div className="chat-message__hover-actions">
              <FlagMenu myFlag={message.myFlag ?? null} onSetFlag={(kind) => actions.setFlag(message.id, kind)} />
              <RemindMenu onRemind={(at) => actions.remind(message.id, at)} />
              <EmojiPickerPopover
                open={reactingId === message.id}
                onClose={() => setReactingId(null)}
                label={`React to message from ${authorName}`}
                alignRight
                below
                onPick={(emoji) => {
                  actions.toggleReaction(message.id, emoji);
                  setReactingId(null);
                }}
                trigger={
                  <IconButton
                    icon={<Icon name="emoji" size={14} />}
                    label={`React to message from ${authorName}`}
                    size="sm"
                    onClick={() => setReactingId((id) => (id === message.id ? null : message.id))}
                  />
                }
              />
              {/* Copy and forward act on the attachment, not the message
                  text, but sit in this same row rather than floating a
                  second toolbar over the thumbnail — one hover toolbar per
                  message reads far more predictably than two that can
                  overlap depending on what the attachment happens to be. */}
              {message.attachments?.[0] && (
                <>
                  {INLINE_IMAGE_TYPES.has(message.attachments[0].contentType) && (
                    <IconButton
                      icon={<Icon name="copy" size={14} />}
                      label={`Copy ${message.attachments[0].fileName} to clipboard`}
                      size="sm"
                      onClick={() => {
                        const attachment = message.attachments![0]!;
                        void copyImageToClipboard(attachment.id, attachment.contentType)
                          .then(() => toast("Image copied to clipboard."))
                          .catch(() => toast("Could not copy that image.", { tone: "danger" }));
                      }}
                    />
                  )}
                  <IconButton
                    icon={<Icon name="forward" size={14} />}
                    label={`Forward ${message.attachments[0].fileName}`}
                    size="sm"
                    onClick={() => actions.forward(message)}
                  />
                </>
              )}
              {!message.pending && !message.failed && (
                <>
                  {!message.attachments?.[0] && (
                    <IconButton
                      icon={<Icon name="forward" size={14} />}
                      label={`Forward message from ${authorName}`}
                      size="sm"
                      onClick={() => actions.forward(message)}
                    />
                  )}
                  <IconButton
                    icon={<Icon name="chat" size={14} />}
                    label={`Reply in thread to ${authorName}`}
                    size="sm"
                    onClick={() => actions.openThread(message)}
                  />
                  <IconButton
                    icon={<Icon name="archive" size={14} />}
                    label={message.isSaved ? "Remove from saved items" : "Save for later"}
                    size="sm"
                    variant={message.isSaved ? "soft" : "ghost"}
                    aria-pressed={!!message.isSaved}
                    onClick={() => actions.toggleSaved(message.id, !message.isSaved)}
                  />
                  {canPin && (
                    <IconButton
                      icon={<Icon name="pin" size={14} />}
                      label={message.pinned ? "Unpin this message" : "Pin this message"}
                      size="sm"
                      variant={message.pinned ? "soft" : "ghost"}
                      aria-pressed={!!message.pinned}
                      onClick={() => actions.togglePin(message.id, !message.pinned)}
                    />
                  )}
                </>
              )}
              {/* Not offered on a message that has not been accepted yet: there
                  is no server id to reply to until it has. */}
              {!message.pending && !message.failed && (
                <IconButton
                  icon={<Icon name="reply" size={14} />}
                  label={`Reply to message from ${authorName}`}
                  size="sm"
                  onClick={() => actions.reply(message)}
                />
              )}
              {/* Only where a task can meaningfully live, and only once — a
                  message that already spawned one shows its card instead. */}
              {canRaiseTask && !message.linkedTask && (
                <IconButton
                  icon={<Icon name="check" size={14} />}
                  label={`Create a task from this message`}
                  size="sm"
                  onClick={() => actions.raiseTask(message)}
                />
              )}
              {/*
                * Editing and deleting are offered on your own messages only.
                * An administrator can still delete anyone's — the server allows
                * it — but that belongs in a moderation surface rather than as a
                * button on every message in the room.
                */}
              {isOwn && !message.pending && !message.failed && (
                <>
                  <IconButton
                    icon={<Icon name="edit" size={14} />}
                    label="Edit this message"
                    size="sm"
                    onClick={() => actions.edit(message)}
                  />
                  <IconButton
                    icon={<Icon name="trash" size={14} />}
                    label="Delete this message"
                    size="sm"
                    onClick={() => actions.delete(message)}
                  />
                </>
              )}
            </div>

            {message.thread && message.thread.replyCount > 0 && (
              <button type="button" className="chat-message__thread" onClick={() => actions.openThread(message)}>
                <span className="chat-message__thread-avatars">
                  {message.thread.participants.map((p) => (
                    <Avatar key={p.id} name={p.displayName} src={p.avatarUrl ?? undefined} size={18} />
                  ))}
                </span>
                <strong>
                  {message.thread.replyCount} {message.thread.replyCount === 1 ? "reply" : "replies"}
                </strong>
                {message.thread.lastReplyAt && <span>Last reply {formatClock(message.thread.lastReplyAt)}</span>}
              </button>
            )}

            {!!message.reactions?.length && (
              <div className="chat-message__reactions">
                {message.reactions.map((reaction) => (
                  <button
                    key={reaction.emoji}
                    type="button"
                    className={`chat-reaction-chip ${reaction.reacted ? "is-reacted" : ""}`}
                    onClick={() => actions.toggleReaction(message.id, reaction.emoji)}
                    aria-pressed={reaction.reacted}
                  >
                    <span aria-hidden="true">{reaction.emoji}</span>
                    <span>{reaction.count}</span>
                  </button>
                ))}
              </div>
            )}

            {/* Marks a message whose text is no longer the one that was sent.
                The column has existed since the first schema and until now
                nothing could ever set it. */}
            {message.editedAt && <span className="chat-message__edited">edited</span>}

            {message.failed && (
              <button
                type="button"
                className="chat-message__status chat-message__status--retry"
                onClick={() => actions.retry(message)}
              >
                Failed to send · tap to retry
              </button>
            )}
            {message.pending && !message.failed && <span className="chat-message__status">Sending…</span>}

            {/*
              * Delivery ticks, on your own messages only — a tick on someone
              * else's message would be claiming to know what you did, which
              * the sender already knows.
              *
              * One tick means the server has it; two means everyone else in
              * the channel has read past it. Not shown while pending: there is
              * nothing to report until the send succeeds.
              */}
            {isOwn && !message.pending && !message.failed && (
              <span
                className={`chat-message__ticks ${readWatermark && message.id <= readWatermark ? "is-read" : ""}`}
                title={readWatermark && message.id <= readWatermark ? "Read by everyone" : "Sent"}
              >
                <Icon name="check" size={12} />
                {readWatermark && message.id <= readWatermark && <Icon name="check" size={12} />}
              </span>
            )}
          </div>
          ),
        )}
      </div>
      <AttachmentViewer viewing={viewing} onClose={() => setViewing(null)} />
    </div>
  );
}
